import React, { useState } from 'react';
import axios from 'axios';
import { X, Share2, Activity, Phone, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const SRModal = ({ onClose, initialData }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [loading, setLoading] = useState(false);
  
  // 🎯 تهيئة البيانات لتتطابق تماماً مع الدالة الأصلية في الباك إند
  const [formData, setFormData] = useState({
    id: 'new', // 👈 ضروري عشان الباك إند يعرف إنه إنشاء جديد
    description: '',
    // استغلال الوصف الطويل لربط طلب الخدمة بأمر الشغل المصدر
    long_description: initialData?.wonum ? `Originating Work Order: ${initialData.wonum}\n` : '',
    priority: 3,
    phone: '',
    email: '',
    assetnum: initialData?.assetnum || '',
    location_id: initialData?.location_id || '', // 👈 إضافة الـ Location
    site_id: initialData?.site_id || '',
    org_id: initialData?.org_id || ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description) {
      return toast.error(t('common.required_fields', 'Please fill required fields'));
    }
    
    setLoading(true);
    const loadId = toast.loading(t('common.saving', 'Saving...'));
    
    try {
      const token = localStorage.getItem('token');
      // 🎯 التوجيه للمسار الحقيقي
      const res = await axios.post('/api/sr/save', formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(`${t('common.save_success', 'Saved Successfully')} - ${res.data.srnum}`, { id: loadId });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.saveFail', 'Failed to save'), { id: loadId });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className={`bg-surface-card w-full max-w-xl rounded-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 ${isRTL ? 'text-right' : 'text-left'}`}>

        {/* Header */}
        <div className={`bg-primary-dark p-4 flex justify-between items-center text-ink-inverse `}>
          <div className={`flex items-center gap-3 `}>
            <Share2 className="text-ink-inverse/70" />
            <h2 className="text-sm font-black uppercase tracking-widest">
              {t('sidebar.sr', 'Create Service Request')}
            </h2>
          </div>
          <button onClick={onClose} className="hover:bg-ink-inverse/20 p-1 rounded transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 bg-surface-panel space-y-4">
           {/* Description (Short) */}
           <div>
             <label className="text-[10px] font-black text-ink-secondary uppercase tracking-widest">
               {t('wo.fields.description', 'Summary')} <span className="text-status-danger">*</span>
             </label>
             <input
               type="text"
               className={`w-full mt-1 p-2 border border-line rounded outline-none focus:border-primary shadow-sm text-sm font-bold text-ink ${isRTL ? 'text-right' : 'text-left'}`}
               value={formData.description}
               onChange={e => setFormData({...formData, description: e.target.value})}
               placeholder={t('wo.placeholders.desc', 'Enter short summary...')}
               required
             />
           </div>

           {/* Long Description */}
           <div>
             <label className="text-[10px] font-black text-ink-secondary uppercase tracking-widest">
               {t('common.details', 'Details')}
             </label>
             <textarea
               className={`w-full mt-1 p-3 border border-line rounded outline-none focus:border-primary shadow-sm text-sm font-bold text-ink ${isRTL ? 'text-right' : 'text-left'}`}
               value={formData.long_description}
               onChange={e => setFormData({...formData, long_description: e.target.value})}
               rows="3"
               placeholder="..."
             />
           </div>

           {/* Contact Info & Priority */}
           <div className={`grid grid-cols-1 md:grid-cols-3 gap-4`}>
             <div className="flex flex-col">
               <label className="text-[10px] font-black text-ink-secondary uppercase tracking-widest flex items-center gap-1 mb-1">
                 <Phone size={12} /> {t('common.phone', 'Phone')}
               </label>
               <input
                 type="text"
                 className="p-2 border border-line rounded outline-none focus:border-primary text-sm font-bold text-ink bg-surface-card"
                 value={formData.phone}
                 onChange={e => setFormData({...formData, phone: e.target.value})}
               />
             </div>

             <div className="flex flex-col">
               <label className="text-[10px] font-black text-ink-secondary uppercase tracking-widest flex items-center gap-1 mb-1">
                 <Mail size={12} /> {t('common.email', 'Email')}
               </label>
               <input
                 type="email"
                 className="p-2 border border-line rounded outline-none focus:border-primary text-sm font-bold text-ink bg-surface-card"
                 value={formData.email}
                 onChange={e => setFormData({...formData, email: e.target.value})}
               />
             </div>

             <div className="flex flex-col">
               <label className="text-[10px] font-black text-ink-secondary uppercase tracking-widest mb-1">
                 {t('wo.fields.priority', 'Priority')}
               </label>
               <select
                 className="p-2 border border-line rounded outline-none focus:border-primary text-sm font-bold text-ink bg-surface-card"
                 value={formData.priority}
                 onChange={e => setFormData({...formData, priority: parseInt(e.target.value)})}
               >
                 <option value={1}>1 - Urgent</option>
                 <option value={2}>2 - High</option>
                 <option value={3}>3 - Medium</option>
                 <option value={4}>4 - Low</option>
               </select>
             </div>
           </div>
           
           {/* Footer Buttons */}
           <div className={`flex justify-end pt-4 border-t border-line mt-6 `}>
             <button
               type="button"
               onClick={onClose}
               className="px-4 py-2 text-xs font-bold text-ink-secondary hover:text-ink uppercase transition-colors"
             >
               {t('common.cancel', 'Cancel')}
             </button>
             <button
               type="submit"
               disabled={loading}
               className="bg-primary hover:bg-primary-hover text-ink-inverse px-6 py-2 rounded font-black uppercase text-xs flex items-center gap-2 shadow-md transition-all active:scale-95 disabled:opacity-70"
             >
               {loading ? <Activity className="animate-spin" size={14} /> : null} 
               {t('common.save', 'Save')}
             </button>
           </div>
        </form>
      </div>
    </div>
  );
};

export default SRModal;